import React from "react";
import { Check } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { IDR } from "@/lib/format";
import { labelClass } from "@/components/Brand";

/**
 * Checkbox grid of the sizes in the studio's master price catalogue, so an admin
 * never retypes a size code. `catalog` is { "12x18 in (SC)": 70000, ... }.
 */
export const PrintSizePicker = ({ catalog, selected, onChange, testIdPrefix = "print-size" }) => {
  const { t } = useI18n();
  const sizes = Object.keys(catalog || {});
  const chosen = selected || [];
  // Legacy projects can hold size codes that predate the catalogue; those get no price.
  const orphans = chosen.filter((s) => !(s in (catalog || {})));

  const toggle = (size) =>
    onChange(chosen.includes(size) ? chosen.filter((s) => s !== size) : [...chosen, size]);

  if (sizes.length === 0) {
    return (
      <div>
        <label className={labelClass}>{t("print_sizes")}</label>
        <p className="mt-2 text-xs text-[#707070]" data-testid={`${testIdPrefix}-empty`}>
          {t("no_catalog_sizes")}
        </p>
      </div>
    );
  }

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <label className={labelClass}>{t("print_sizes")}</label>
        <span className="font-mono-accent text-[10px] tracking-[0.14em] uppercase text-[#707070]"
          data-testid={`${testIdPrefix}-count`}>
          {t("n_selected", { n: chosen.length })}
        </span>
      </div>
      <p className="mt-1 mb-3 text-[11px] text-[#707070]">{t("print_sizes_hint")}</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#2A2A2A] border border-[#2A2A2A]">
        {sizes.map((size) => {
          const on = chosen.includes(size);
          return (
            <button key={size} type="button" data-testid={`${testIdPrefix}-option-${size}`}
              onClick={() => toggle(size)} aria-pressed={on}
              className={`flex items-center gap-3 text-left px-4 py-3 min-h-[48px] transition-colors ${
                on ? "bg-[#D4AF37]/[0.08]" : "bg-[#0B0B0B] hover:bg-[#121212]"
              }`}>
              <span className={`w-5 h-5 shrink-0 border flex items-center justify-center transition-colors ${
                on ? "bg-[#D4AF37] border-[#D4AF37] text-black" : "border-[#3a3a3a]"
              }`}>
                {on && <Check size={13} strokeWidth={3} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className={`block text-sm truncate ${on ? "text-[#F3F3F1]" : "text-[#A8A8A8]"}`}>{size}</span>
                <span className="block font-mono-accent text-[10px] text-[#707070]">{IDR(catalog[size])}</span>
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap gap-4">
        <button type="button" data-testid={`${testIdPrefix}-select-all`} onClick={() => onChange(sizes)}
          className="text-[10px] tracking-[0.18em] uppercase text-[#D4AF37] hover:underline">
          {t("select_all")}
        </button>
        <button type="button" data-testid={`${testIdPrefix}-clear`} onClick={() => onChange([])}
          className="text-[10px] tracking-[0.18em] uppercase text-[#707070] hover:text-[#F3F3F1]">
          {t("clear")}
        </button>
      </div>

      {orphans.length > 0 && (
        <div className="mt-4 border-l-2 border-[#8B0000] pl-4 py-1" data-testid={`${testIdPrefix}-orphan-warning`}>
          <p className="text-xs text-[#F3F3F1]">{t("unpriced_sizes_title")}</p>
          <p className="mt-1.5 font-mono-accent text-[11px] text-[#D4AF37]">{orphans.join(" · ")}</p>
          <p className="mt-2 text-[11px] text-[#A8A8A8] leading-relaxed">{t("unpriced_sizes_body")}</p>
        </div>
      )}
    </div>
  );
};
